import type { CSSProperties } from 'react'

import { cn } from '@/lib/utils'

/**
 * How fast a sweep crosses: a sparkline's quick trace, or a full chart's draw
 * on the in-out curve the canvas charts trace on.
 */
const PACES = {
  sparkline: { animationDuration: 'var(--motion-sparkline-draw)' },
  chart: {
    animationDuration: 'var(--motion-chart-draw)',
    animationTimingFunction: 'var(--ease-in-out)',
  },
} satisfies Record<string, CSSProperties>

interface SweepClipProps {
  /** The `clipPath` id the drawn group points at (`clipPath="url(#id)"`). */
  id: string
  /** The clip's box, in the drawing's own units: the whole plot, plus any bleed a stroke needs. */
  x?: number
  y?: number
  width: number
  height: number
  pace: keyof typeof PACES
  /**
   * Left out, the sweep plays once, on mount. A string replays it each time it
   * changes (a window switch); `null` shows the line whole, so a placeholder's
   * remount does not sweep it again.
   */
  drawKey?: string | null | undefined
}

/**
 * A line chart's draw-in: a clip that grows from the left across the plot, so
 * the line, its area and anything else under the clip trace in together.
 *
 * A clip, not `animate-draw`'s dash: under a stretched viewBox the stroke is
 * non-scaling, and Chrome measures a `pathLength` dash in the path's own units
 * but strokes it in the screen's, so the dash ran out a fraction of the way
 * along. CSS alone, on mount alone: a poll that hands over new points changes
 * the path, not the clip, so nothing replays. Reduced motion collapses it
 * (globals.css).
 */
export default function SweepClip({ id, x, y, width, height, pace, drawKey }: SweepClipProps) {
  return (
    <clipPath id={id}>
      <rect
        key={drawKey === undefined ? undefined : (drawKey ?? 'settled')}
        x={x}
        y={y}
        width={width}
        height={height}
        className={cn('origin-left transform-fill', drawKey !== null && 'animate-grow-x')}
        style={PACES[pace]}
      />
    </clipPath>
  )
}
